import { useLazyGetTemplateInfoQuery } from '@/services/contractTemplates';
import { FormikTextInput } from '@palmetto/palmetto-components';
import { Field, useField } from 'formik';
import { useRef, useState } from 'react';

import { DOCUSIGN_TEMPLATE_ID_REGEX } from './contractTemplateSchema';

interface DocuSignTemplateIdFieldProps {
  label: string;
  name: string;
  id: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  /** Shown instead of the resolved DocuSign template name. */
  helpText?: string;
}

interface ValidationCache {
  value: string;
  result: string | undefined;
  templateName: string;
}

export const DocuSignTemplateIdField = ({ name, helpText, ...props }: DocuSignTemplateIdFieldProps) => {
  const [field, meta] = useField({ name });
  const [templateName, setTemplateName] = useState('');
  const [isValidatingDocuSign, setIsValidatingDocuSign] = useState(false);
  const [getTemplateInfo] = useLazyGetTemplateInfoQuery();
  const validationCacheRef = useRef<ValidationCache | null>(null);

  const validateDocuSignTemplate = async (value: string | undefined): Promise<string | undefined> => {
    const trimmedValue = value?.trim() ?? '';

    // Clear template name for empty/invalid format - let Yup handle required validation
    if (!trimmedValue || !DOCUSIGN_TEMPLATE_ID_REGEX.test(trimmedValue)) {
      setTemplateName('');
      validationCacheRef.current = null;
      return undefined;
    }

    // Return cached result if value hasn't changed
    if (validationCacheRef.current?.value === trimmedValue) {
      setTemplateName(validationCacheRef.current.templateName);
      return validationCacheRef.current.result;
    }

    setIsValidatingDocuSign(true);
    setTemplateName('');

    try {
      const result = await getTemplateInfo({ templateId: trimmedValue }, true).unwrap();
      setIsValidatingDocuSign(false);

      if (result?.name) {
        setTemplateName(result.name);
        validationCacheRef.current = {
          value: trimmedValue,
          result: undefined,
          templateName: result.name,
        };
        return undefined;
      }

      const error = 'Template not found in DocuSign';
      validationCacheRef.current = {
        value: trimmedValue,
        result: error,
        templateName: '',
      };
      return error;
    } catch {
      setIsValidatingDocuSign(false);
      const error = 'Template not found in DocuSign';
      validationCacheRef.current = {
        value: trimmedValue,
        result: error,
        templateName: '',
      };
      return error;
    }
  };

  const getHelpText = () => {
    if (helpText) return helpText;
    if (isValidatingDocuSign) return 'Validating...';
    if (templateName) return templateName;
    return undefined;
  };

  return (
    <Field
      {...field}
      {...props}
      name={name}
      type="text"
      validate={props.isDisabled ? undefined : validateDocuSignTemplate}
      helpText={getHelpText()}
      error={meta.touched && meta.error}
      component={FormikTextInput}
      placeholder="Add DocuSign Template ID"
      isRequired={props.isRequired ?? false}
    />
  );
};
